import React from 'react';

interface Props {
  isOpen: boolean;
  ticker?: string;
  tickers?: string[];
  onConfirm: () => void;
  onCancel: () => void;
  onExclude?: () => void; // For CSV upload - exclude from upload
}

/**
 * NonTickerConfirmationDialog
 * 
 * Modal dialog for confirming non-ticker addition with uniform wording for all use cases.
 * Supports both single ticker (transaction form) and multiple tickers (CSV upload).
 */
const NonTickerConfirmationDialog: React.FC<Props> = ({
  isOpen,
  ticker,
  tickers,
  onConfirm,
  onCancel,
  onExclude,
}) => {
  if (!isOpen) return null;

  const isMultiple = !!tickers && tickers.length > 0;
  const displayTickers = isMultiple ? tickers! : (ticker ? [ticker] : []);

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 flex items-center justify-center bg-black bg-opacity-30 z-[10001]">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Ticker Not Found</h2>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        
        <div className="mb-4">
          {isMultiple ? (
            <p className="text-sm text-gray-700 mb-2">
              We don't have any data on the following tickers:{' '}
              <span className="font-medium">{displayTickers.join(', ')}</span>.
            </p>
          ) : (
            <p className="text-sm text-gray-700 mb-2">
              This ticker is not in our database. Do you want to add it as a custom ticker or edit your submission?
            </p>
          )}
          
          <p className="text-xs text-gray-500 mt-2">
            Note: Custom tickers won't have any historical data, but you can add all transaction types for this ticker in your portfolio.
          </p>
        </div>

        <div className="flex items-center justify-end space-x-3">
          {isMultiple && onExclude ? (
            <>
              <button
                onClick={onCancel}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 h-[3.5rem] flex items-center justify-center"
              >
                Cancel
              </button>
              <button
                onClick={onExclude}
                className="px-4 py-2 bg-green-100 text-green-800 rounded hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-green-500 border border-green-300 h-[3.5rem] flex items-center justify-center"
              >
                Exclude from upload
              </button>
              <button
                onClick={onConfirm}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 h-[3.5rem] flex items-center justify-center"
              >
                Add these as customer tickers
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onCancel}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Edit submission
              </button>
              <button
                onClick={onConfirm}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Add as custom ticker
              </button>
            </>
          )}
          {isMultiple && !onExclude && (
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default NonTickerConfirmationDialog;
